import { App, type URLOpenListenerEvent } from "@capacitor/app";

import { Platform } from "./platform";

/**
 * Normalise a deep link URL to a router-compatible path.
 *
 * Strips the custom URL scheme, preserving the full pathname and search string
 * so that deep links like `clipboardworks.workplace:///login/verify?authUrl=...`
 * are passed through to the router as `/login/verify?authUrl=...`.
 */
function normaliseDeepLink(rawUrl: string): string {
  const urlObject = new URL(rawUrl);
  return `${urlObject.pathname}${urlObject.search}` || "/";
}

/**
 * localStorage key for time-bounded deep link deduplication.
 *
 * Capacitor's native-to-web bridge re-delivers the last appUrlOpen event
 * whenever the bridge reconnects — which happens on every
 * window.location.reload(). Without deduplication, a page reload (e.g.
 * from account switching) replays the original magic-link URL, navigating
 * to AuthRedirect with an expired token.
 *
 * Stores `{ url, ts }` JSON so that identical URLs are only suppressed
 * within a short time window. After the window expires, the same URL is
 * treated as a new deep link (handles edge cases like re-sent magic links).
 *
 * Uses localStorage because Capacitor's WKWebView does not reliably
 * persist sessionStorage across reloads.
 */
const LAST_HANDLED_DEEP_LINK_KEY = "deepLinkLastHandledUrl";

/** Suppress duplicate deep links within this window (ms). */
const DEDUPE_WINDOW_MS = 30_000;

interface DeepLinkEntry {
  url: string;
  ts: number;
}

function isDuplicate(rawUrl: string): boolean {
  try {
    const stored = localStorage.getItem(LAST_HANDLED_DEEP_LINK_KEY);
    if (!stored) {
      return false;
    }
    const entry: DeepLinkEntry = JSON.parse(stored);
    return entry.url === rawUrl && Date.now() - entry.ts <= DEDUPE_WINDOW_MS;
  } catch {
    return false;
  }
}

function markHandled(rawUrl: string): void {
  const entry: DeepLinkEntry = { url: rawUrl, ts: Date.now() };
  localStorage.setItem(LAST_HANDLED_DEEP_LINK_KEY, JSON.stringify(entry));
}

/**
 * Initialize deep link listener for Capacitor mobile app.
 * Handles both app launch URLs and URLs received while app is running.
 *
 * Deep links use a custom URL scheme: clipboardworks.workplace:///path?params
 * Example: clipboardworks.workplace:///login/verify?authUrl=...
 */
export function initializeDeepLinkHandler(onDeepLink: (url: string) => void): () => void {
  if (!Platform.isMobile) {
    return () => {
      // No-op for web
    };
  }

  let cleanup: (() => void) | undefined;

  void (async () => {
    // Check if app was launched via deep link
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url && !isDuplicate(launchUrl.url)) {
      markHandled(launchUrl.url);
      onDeepLink(normaliseDeepLink(String(launchUrl.url)));
    }

    // Listen for deep links while app is running. Deduplicate against
    // the last handled URL within a time window to prevent stale replays
    // after page reloads (Capacitor's bridge re-delivers the last event
    // on reconnect).
    const listener = await App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      if (isDuplicate(event.url)) {
        return;
      }
      markHandled(event.url);
      onDeepLink(normaliseDeepLink(String(event.url)));
    });

    cleanup = () => {
      void listener.remove();
    };
  })();

  return () => {
    cleanup?.();
  };
}
